import type { User } from "firebase/auth";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { dayText } from "./accounts";
import * as api from "./api";
import { type CalendarForm, calendarChange, calendarForm, MARGIN_MAX, RISK_MAX, RISK_MIN, RULE_TEXT, WEEKDAYS } from "./calendar";
import { Skeleton } from "./Skeleton";

/** Design 8.1 and 8.5: the week start, the cycle start, the holiday rule and the early-pull margin. */
export function CalendarSettings({ user }: { user: User }) {
  const [found, setFound] = useState<api.CalendarSettings | "failed" | undefined>(undefined);
  const [form, setForm] = useState<CalendarForm | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [problem, setProblem] = useState<{ key: keyof CalendarForm; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const status = useRef<HTMLParagraphElement>(null);
  const inputs = useRef<Partial<Record<keyof CalendarForm, HTMLInputElement | HTMLSelectElement | null>>>({});

  useEffect(() => {
    if (problem) inputs.current[problem.key]?.focus();
  }, [problem]);

  useEffect(() => {
    user
      .getIdToken()
      .then(api.calendarSettings)
      .then(
        (next) => {
          setFound(next);
          setForm(calendarForm(next.calendar));
        },
        () => setFound("failed"),
      );
  }, [user]);

  if (found === "failed") {
    return (
      <fieldset>
        <legend>Cycle calendar</legend>
        <p className="error">Cannot load the cycle calendar. Try again later.</p>
      </fieldset>
    );
  }
  if (found === undefined || form === null) {
    return (
      <fieldset aria-busy="true">
        <legend>Cycle calendar</legend>
        <Skeleton shape="form" rows={12} label="Loading the cycle calendar" />
      </fieldset>
    );
  }
  const save = async (event: FormEvent) => {
    event.preventDefault();
    const change = calendarChange(form);
    if ("key" in change) {
      setMessage(null);
      setProblem(change);
      return;
    }
    setProblem(null);
    setSaving(true);
    setMessage(null);
    try {
      const next = await api.setCalendarSettings(await user.getIdToken(), change);
      setFound(next);
      setForm(calendarForm(next.calendar));
      setMessage({ ok: true, text: "Saved. The cycle board and the next visits use the new calendar." });
    } catch (e) {
      const detail = e instanceof api.ApiError && e.status < 500 ? e.detail : undefined;
      setMessage({ ok: false, text: detail ?? "The cycle calendar was not saved. Try again." });
    } finally {
      setSaving(false);
      status.current?.focus();
    }
  };
  const edit = (next: Partial<CalendarForm>) => {
    setForm({ ...form, ...next });
    setMessage(null);
    setProblem(null);
  };
  const defaults = found.defaults;
  const error = (key: keyof CalendarForm) =>
    problem?.key === key ? (
      <p id={`calendar-${key}-error`} role="alert" className="error" data-testid={`calendar-${key}-error`}>
        {problem.text}
      </p>
    ) : null;
  const described = (key: keyof CalendarForm) => (problem?.key === key ? `calendar-${key}-error calendar-${key}-hint` : `calendar-${key}-hint`);
  return (
    <form onSubmit={save} noValidate data-testid="calendar-settings">
      <fieldset disabled={!found.can_edit || saving}>
        <legend>Cycle calendar</legend>
        <div className="limit">
          <label htmlFor="calendar-week-starts">First day of a week</label>
          <select
            id="calendar-week-starts"
            data-testid="calendar-week-starts"
            ref={(element) => {
              inputs.current.weekStarts = element;
            }}
            value={form.weekStarts}
            aria-describedby={described("weekStarts")}
            onChange={(e) => edit({ weekStarts: e.target.value })}
          >
            {WEEKDAYS.map((name, day) => (
              <option key={name} value={day}>
                {name}
              </option>
            ))}
          </select>
          {error("weekStarts")}
          <p id="calendar-weekStarts-hint" className="hint">
            Default: {WEEKDAYS[defaults.week_starts]}.
          </p>
        </div>
        <div className="limit">
          <label htmlFor="calendar-cycle-start">First day of cycle 1</label>
          <input
            id="calendar-cycle-start"
            data-testid="calendar-cycle-start"
            type="date"
            ref={(element) => {
              inputs.current.cycleStart = element;
            }}
            value={form.cycleStart}
            aria-invalid={problem?.key === "cycleStart" || undefined}
            aria-describedby={described("cycleStart")}
            onChange={(e) => edit({ cycleStart: e.target.value })}
          />
          {error("cycleStart")}
          <p id="calendar-cycleStart-hint" className="hint">
            Rotation week 1 starts on this day, and each 4 weeks after it. A change moves every rotation week. Default: {dayText(defaults.cycle_start, true)}.
          </p>
        </div>
        <div className="limit">
          <label htmlFor="calendar-rule">A stop on a holiday moves to</label>
          <select
            id="calendar-rule"
            data-testid="calendar-rule"
            ref={(element) => {
              inputs.current.rule = element;
            }}
            value={form.rule}
            aria-describedby={described("rule")}
            onChange={(e) => edit({ rule: e.target.value as CalendarForm["rule"] })}
          >
            {(Object.keys(RULE_TEXT) as CalendarForm["rule"][]).map((rule) => (
              <option key={rule} value={rule}>
                {RULE_TEXT[rule]}
              </option>
            ))}
          </select>
          {error("rule")}
          <p id="calendar-rule-hint" className="hint">
            Workdays are Monday to Friday, less the holidays. Default: {RULE_TEXT[defaults.holiday_rule].toLowerCase()}.
          </p>
        </div>
        <div className="limit">
          <label htmlFor="calendar-margin">Early-pull margin (%)</label>
          <input
            id="calendar-margin"
            data-testid="calendar-margin"
            inputMode="decimal"
            ref={(element) => {
              inputs.current.margin = element;
            }}
            value={form.margin}
            aria-invalid={problem?.key === "margin" || undefined}
            aria-describedby={described("margin")}
            onChange={(e) => edit({ margin: e.target.value })}
          />
          {error("margin")}
          <p id="calendar-margin-hint" className="hint">
            An account is an early pull when its estimated load at the next visit is above 100% less this margin. From 0% to {MARGIN_MAX}%. Default:{" "}
            {Math.round(defaults.early_margin * 100)}%.
          </p>
        </div>
        <div className="limit">
          <label htmlFor="calendar-risk">Early-pull chance of overflow (%)</label>
          <input
            id="calendar-risk"
            data-testid="calendar-risk"
            inputMode="decimal"
            ref={(element) => {
              inputs.current.risk = element;
            }}
            value={form.risk}
            aria-invalid={problem?.key === "risk" || undefined}
            aria-describedby={described("risk")}
            onChange={(e) => edit({ risk: e.target.value })}
          />
          {error("risk")}
          <p id="calendar-risk-hint" className="hint">
            With a fill estimate, an account is an early pull when its chance of overflow by the next visit is this much or more. From {RISK_MIN}% to {RISK_MAX}%.
            Default: {Math.round(defaults.early_risk * 100)}%.
          </p>
        </div>
        {found.can_edit && (
          <div className="actions">
            <button className="primary" data-testid="save-calendar">
              {saving ? "Saving…" : "Save the calendar"}
            </button>
          </div>
        )}
      </fieldset>
      <p ref={status} tabIndex={-1} role="status" data-testid="calendar-status" className={message && !message.ok ? "error" : undefined}>
        {message?.text}
      </p>
    </form>
  );
}
